/**
 * Analysis Result View Component
 * Comprehensive display of analysis results with visualizations
 */

import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { analysisAPI } from "../../utils/analysisAPI";
import { showToast } from "../../components/Toast";
import { showConfirm } from "../../components/ConfirmModal";
import "./AnalysisResultView.css";

const AnalysisResultView = () => {
  const { analysisId } = useParams();
  const navigate = useNavigate();

  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [executing, setExecuting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [showReportPreview, setShowReportPreview] = useState(false);
  const [showExportPreview, setShowExportPreview] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    loadAnalysis();
    // Poll for updates if running
    let interval;
    if (analysis?.status === "RUNNING") {
      interval = setInterval(loadAnalysis, 2000);
    }
    return () => clearInterval(interval);
  }, [analysisId, analysis?.status]);

  const loadAnalysis = async () => {
    try {
      if (!analysis) setLoading(true);
      setRefreshing(true);
      const data = await analysisAPI.getAnalysis(analysisId);
      setAnalysis(data);
      setError(null);

      // Show toast if analysis changed to FAILED
      if (
        data.status === "FAILED" &&
        analysis?.status !== "FAILED" &&
        data.error_message
      ) {
        showToast.error("Analysis failed: " + data.error_message);
      }
    } catch (err) {
      showToast.error("Failed to load analysis: " + err.message);
      setError(err.message);
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  };

  const handleExecute = async () => {
    try {
      setExecuting(true);
      const updated = await analysisAPI.executeAnalysis(analysisId);
      setAnalysis(updated);
      setError(null);
      showToast.success("Analysis started successfully");
    } catch (err) {
      showToast.error("Failed to execute: " + err.message);
      setError(null);
    } finally {
      setExecuting(false);
    }
  };

  const handleDelete = async () => {
    const confirmed = await showConfirm({
      title: "Delete Analysis?",
      message:
        "This action cannot be undone. All analysis data will be permanently deleted.",
      confirmText: "Delete",
      cancelText: "Cancel",
      type: "danger",
    });

    if (!confirmed) return;

    try {
      await analysisAPI.deleteAnalysis(analysisId);
      showToast.success("Analysis deleted successfully");
      navigate("/analyst/analysis");
    } catch (err) {
      showToast.error("Failed to delete: " + err.message);
    }
  };

  const getStatusIcon = (status) => {
    const icons = {
      DRAFT: { icon: null, color: "#6c757d" },
      PENDING: { icon: null, color: "#fd7e14" },
      RUNNING: { icon: null, color: "#0d6efd" },
      COMPLETED: { icon: "✓", color: "#198754" },
      FAILED: { icon: "✗", color: "#dc3545" },
    };
    return icons[status] || icons.DRAFT;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDuration = (seconds) => {
    if (!seconds) return "-";
    if (seconds < 60) return `${seconds}s`;
    return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
  };

  if (loading) {
    return (
      <div className="analysis-result-view">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading analysis details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="analysis-result-view">
        <div className="error-container">
          <div className="alert alert-error">{error}</div>
          <button
            onClick={() => navigate("/analyst/analysis")}
            className="btn btn-primary"
          >
            ← Back to Analyses
          </button>
        </div>
      </div>
    );
  }

  if (!analysis) {
    return (
      <div className="analysis-result-view">
        <div className="error-container">
          <p>Analysis not found</p>
          <button
            onClick={() => navigate("/analyst/analysis")}
            className="btn btn-primary"
          >
            Back to Analyses
          </button>
        </div>
      </div>
    );
  }

  const statusInfo = getStatusIcon(analysis.status);

  return (
    <div className="analysis-result-view">
      {/* Header */}
      <div className="result-header">
        <div className="header-top">
          <button
            className="btn-back"
            onClick={() => navigate("/analyst/analysis")}
          >
            ← Back
          </button>
          <h1>{analysis.data_table || analysis.client_name || analysis.dataset || `Analysis #${analysis.id}`}</h1>
          <div style={{ flex: 1 }}></div>
          {analysis.status === "DRAFT" && (
            <button className="btn btn-danger btn-sm" onClick={handleDelete}>
              Delete
            </button>
          )}
        </div>

        <div className="header-meta">
          <div className="meta-item">
            <span className="label">Model:</span>
            <span className="value">{analysis.model_name}</span>
          </div>
          <div className="meta-item">
            <span className="label">Category:</span>
            <span className="value">
              {analysis.model_category.replace(/_/g, " ")}
            </span>
          </div>
          <div className="meta-item">
            <span className="label">Status:</span>
            <span className="value status" style={{ color: statusInfo.color }}>
              {statusInfo.icon ? `${statusInfo.icon} ` : ""}{analysis.status}
            </span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="result-content">
        {/* Left Panel - Configuration */}
        <div className="result-section configuration-section">
          <h3>Configuration</h3>
          <div className="config-grid">
            {analysis.configuration &&
              Object.entries(analysis.configuration).map(([key, value]) => (
                <div key={key} className="config-item">
                  <span className="config-label">{key}:</span>
                  <span className="config-value">
                    {Array.isArray(value) || typeof value === "object"
                      ? JSON.stringify(value)
                      : String(value)}
                  </span>
                </div>
              ))}
          </div>
        </div>

        {/* Timeline Info */}
        <div className="result-section timeline-section">
          <h3>Timeline</h3>
          <div className="timeline">
            <div className="timeline-item">
              <span className="timeline-icon">·</span>
              <div className="timeline-content">
                <div className="timeline-label">Created</div>
                <div className="timeline-time">
                  {formatDate(analysis.created_at)}
                </div>
              </div>
            </div>

            {analysis.started_at && (
              <div className="timeline-item">
                <span className="timeline-icon">·</span>
                <div className="timeline-content">
                  <div className="timeline-label">Started</div>
                  <div className="timeline-time">
                    {formatDate(analysis.started_at)}
                  </div>
                </div>
              </div>
            )}

            {analysis.completed_at && (
              <div className="timeline-item">
                <span className="timeline-icon">✓</span>
                <div className="timeline-content">
                  <div className="timeline-label">Completed</div>
                  <div className="timeline-time">
                    {formatDate(analysis.completed_at)}
                  </div>
                </div>
              </div>
            )}

            {analysis.execution_time_seconds && (
              <div className="timeline-item">
                <span className="timeline-icon">·</span>
                <div className="timeline-content">
                  <div className="timeline-label">Duration</div>
                  <div className="timeline-time">
                    {formatDuration(analysis.execution_time_seconds)}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Execution log (steps) */}
        {analysis.execution_log && analysis.execution_log.length > 0 && (
          <div className="result-section">
            <h3>Execution steps</h3>
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {analysis.execution_log.map((entry, i) => (
                <li
                  key={i}
                  style={{
                    padding: "8px 12px",
                    marginBottom: "6px",
                    background: "var(--bg-grey, #f5f5f5)",
                    borderRadius: "6px",
                    fontSize: "14px",
                  }}
                >
                  {typeof entry === "object" && entry?.message != null ? (
                    <>
                      <strong>{entry.step || "Step"}:</strong> {entry.message}
                    </>
                  ) : (
                    String(entry)
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Large-dataset / sampling notice */}
        {analysis.results?.model_metadata?.sampled_from && (
          <div
            className="alert alert-warning"
            style={{
              background: "var(--warning-bg, #fff3cd)",
              border: "1px solid var(--warning-border, #ffc107)",
              borderRadius: "6px",
              padding: "12px 16px",
              marginBottom: "12px",
              fontSize: "14px",
            }}
          >
            <strong>Large dataset detected.</strong> Training used a random sample of{" "}
            {(200000).toLocaleString()} rows out of{" "}
            {Number(analysis.results.model_metadata.sampled_from).toLocaleString()} total rows.
            Results are based on this sample.
          </div>
        )}

        {/* Results */}
        {analysis.status === "COMPLETED" && analysis.results && (
          <div className="result-section results-section">
            <h3>Results</h3>
            <div className="results-grid">
              {Object.entries(analysis.results).map(([key, value]) => (
                <div key={key} className="result-item">
                  <div className="result-label">{key.replace(/_/g, " ")}</div>
                  <div className="result-value">
                    {typeof value === "object"
                      ? JSON.stringify(value, null, 2)
                      : String(value)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Failed Status - Show error details and Retry Option */}
        {analysis.status === "FAILED" && (
          <div className="result-section failed-section">
            <div style={{ padding: "24px" }}>
              <div style={{ fontSize: "48px", marginBottom: "16px", textAlign: "center" }}>❌</div>
              <h3 style={{ marginBottom: "8px", color: "var(--danger-red, #dc3545)" }}>Analysis Failed</h3>
              <p style={{ color: "var(--text-grey)", marginBottom: "16px" }}>
                The analysis encountered an error during execution. See the details below to understand what went wrong.
              </p>
              {analysis.error_message && (
                <div
                  className="analysis-error-details"
                  style={{
                    background: "var(--bg-grey, #f8f9fa)",
                    border: "1px solid var(--danger-red, #dc3545)",
                    borderRadius: "8px",
                    padding: "16px",
                    marginBottom: "20px",
                    maxHeight: "320px",
                    overflow: "auto",
                  }}
                >
                  <div style={{ fontWeight: 600, marginBottom: "8px", color: "var(--text-dark)" }}>
                    Error details
                  </div>
                  <pre
                    style={{
                      margin: 0,
                      fontSize: "13px",
                      lineHeight: 1.5,
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      color: "var(--text-dark)",
                      fontFamily: "inherit",
                    }}
                  >
                    {analysis.error_message}
                  </pre>
                </div>
              )}
              <div style={{ textAlign: "center" }}>
                <button
                  className="btn btn-primary"
                  onClick={handleExecute}
                  disabled={executing}
                >
                  {executing ? "Retrying..." : "Retry Analysis"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Running State */}
        {analysis.status === "RUNNING" && (
          <div className="result-section running-section">
            <h3>Running</h3>
            <div className="running-message">
              <div className="spinner-mini"></div>
              Analysis is processing...
            </div>
            <button
              className="btn btn-secondary"
              onClick={() => loadAnalysis()}
              disabled={refreshing}
            >
              {refreshing ? "Refreshing..." : "Refresh"}
            </button>
          </div>
        )}

        {/* Actions */}
        <div className="result-section actions-section">
          <h3>Actions</h3>
          <div className="actions-grid">
            {analysis.status === "DRAFT" && (
              <button
                className="btn btn-primary btn-lg"
                onClick={handleExecute}
                disabled={executing}
              >
                {executing ? "Executing..." : "Execute Analysis"}
              </button>
            )}

            {analysis.status === "PENDING" && (
              <button
                className="btn btn-primary btn-lg"
                onClick={() => loadAnalysis()}
                disabled={refreshing}
              >
                {refreshing ? "Checking..." : "Check Status"}
              </button>
            )}

            {analysis.status === "COMPLETED" && (
              <>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    const orgId =
                      analysis.client_id ||
                      analysis.organization_id ||
                      analysis.client?.id;
                    const query = new URLSearchParams();
                    query.set("analysisId", analysis.id);
                    if (orgId) query.set("org", orgId);
                    navigate(`/analyst/design-report?${query.toString()}`);
                  }}
                >
                  Add to Report
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowExportPreview(true);
                    setShowReportPreview(false);
                  }}
                >
                  Export Results
                </button>
              </>
            )}

            {(analysis.status === "FAILED" ||
              analysis.status === "COMPLETED") && (
              <button
                className="btn btn-secondary"
                onClick={() => navigate("/analyst/analysis/new")}
              >
                Create New Analysis
              </button>
            )}

            <button
              className="btn btn-secondary"
              onClick={() => navigate("/analyst/analysis")}
            >
              Back to List
            </button>
          </div>
        </div>
      </div>

      {showReportPreview && (
        <div className="result-section">
          <h3>Report preview</h3>
          <p>
            This analysis can be added to a report. Summary, insights, and
            recommendations are generated from the run results.
          </p>
          <p style={{ marginTop: "12px" }}>
            <button
              className="btn btn-primary"
              onClick={() => navigate(`/analyst/reports?analysisId=${analysis.id}`)}
            >
              Create report from this analysis
            </button>
          </p>
          <div className="results-grid">
            <div className="result-item">
              <div className="result-label">Summary</div>
              <div className="result-value">
                {analysis.results?.summary || "No summary generated for this analysis."}
              </div>
            </div>
            <div className="result-item">
              <div className="result-label">Insights</div>
              <div className="result-value">
                {Array.isArray(analysis.results?.insights)
                  ? analysis.results.insights.join("; ")
                  : analysis.results?.insights || "No insights generated."}
              </div>
            </div>
            <div className="result-item">
              <div className="result-label">Recommendations</div>
              <div className="result-value">
                {Array.isArray(analysis.results?.recommendations)
                  ? analysis.results.recommendations.join("; ")
                  : analysis.results?.recommendations || "No recommendations generated."}
              </div>
            </div>
          </div>
        </div>
      )}

      {showExportPreview && (
        <div className="result-section">
          <h3>Export Results</h3>
          <p>
            Download this analysis as CSV (metrics, config, logs) or PDF report.
          </p>
          <div className="results-grid" style={{ gap: "12px", marginTop: "16px" }}>
            <button
              className="btn btn-primary"
              disabled={exporting}
              onClick={async () => {
                try {
                  setExporting(true);
                  await analysisAPI.exportAnalysis(analysis.id, "csv");
                  showToast.success("CSV downloaded");
                } catch (e) {
                  showToast.error(e.message || "Export failed");
                } finally {
                  setExporting(false);
                }
              }}
            >
              {exporting ? "..." : "Download CSV"}
            </button>
            <button
              className="btn btn-secondary"
              disabled={exporting}
              onClick={async () => {
                try {
                  setExporting(true);
                  await analysisAPI.exportAnalysis(analysis.id, "pdf");
                  showToast.success("PDF downloaded");
                } catch (e) {
                  showToast.error(e.message || "Export failed");
                } finally {
                  setExporting(false);
                }
              }}
            >
              {exporting ? "..." : "Download PDF"}
            </button>
          </div>
          <p style={{ marginTop: "12px", fontSize: "13px", color: "var(--text-grey)" }}>
            Export includes model metrics, configuration, and execution log.
          </p>
        </div>
      )}
    </div>
  );
};

export default AnalysisResultView;
